import { Dropdown, Modal, Col, Row, Space, Typography, message } from "antd";
import { useState } from "react";
import { Guid } from "guid-typescript";
import { SearchQuery } from "../code/search-query/search-query";
import Loader from "../controls/Loader";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import { getTrainingsExport } from "../repository/data.repository";
import TrainingsView from "../components/trainings/TrainingsView";
import { Training } from "../repository/data.default";
import TrainingFormDialog from "../components/trainings/TrainingFormDialog";
import TrainingManageDialog from "../components/trainings/TrainingManageDialog";
import { useCalendarEventPreview } from "../components/useCalendarEventPreview";
import { getProblemMessage } from "../utils/http";

function TrainingsPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [searchQuery, setSearchQuery] = useState<SearchQuery>({
    conditions: [],
  });
  const [trainingInputMode, setTrainingInputMode] = useState<
    "add" | "delete" | "manage"
  >("add");
  const [trainingInputOpen, setTrainingInputOpen] = useState(false);
  const [trainingInputRecord, setTrainingInputRecord] = useState<
    Training | undefined
  >(undefined);
  const [triggerView, setTriggerView] = useState(Guid.create().toString());
  // Deep-link: the backing calendar event behind a training (opened read-only from the Manage dialog).
  const { openPreview, previewDialog } = useCalendarEventPreview();
  const permissions = useSelector(authorizeSelectors.permissions);

  function onSearch(searchQuery: SearchQuery): void {
    setSearchQuery(searchQuery);
    setTriggerView(Guid.create().toString());
  }

  function onTrainingAction(
    mode: "add" | "delete" | "manage",
    record?: Training,
  ): void {
    setTrainingInputRecord(record);
    setTrainingInputMode(mode);
    setTrainingInputOpen(true);
  }

  // A freshly-planned training opens straight in the Manage view so the participants can be invited.
  function onTrainingFormFinish(created?: Training): void {
    setTriggerView(Guid.create().toString());
    if (created) {
      setTrainingInputRecord(created);
      setTrainingInputMode("manage");
      setTrainingInputOpen(true);
    } else {
      setTrainingInputOpen(false);
    }
  }

  function onTrainingInputFinish(): void {
    setTrainingInputOpen(false);
    setTriggerView(Guid.create().toString());
  }

  function onTrainingInputClose(): void {
    setTrainingInputOpen(false);
  }

  function onDownloadExport(): void {
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading trainings export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getTrainingsExport({
      searchQuery,
      pageNumber: 0,
      pageSize: Number.MAX_SAFE_INTEGER,
    })
      .then(() => {
        loading.destroy();
      })
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  return (
    <Col className="relative flex flex-auto flex-col overflow-hidden overflow-y-auto">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Trainings
        </Typography.Title>
        <Space className="flex flex-row">
          <Dropdown.Button
            type="primary"
            disabled={!permissions.can("Trainings", "create")}

            className={`${!permissions.can("Trainings", "create") ? "disabled" : ""}`}
            menu={{
              items: [
                {
                  key: "downloadExport",
                  label: "Export to Excel",
                  disabled: !permissions.can("Trainings", "view"),
                  onClick: onDownloadExport,
                },
              ],
            }}
            onClick={() => onTrainingAction("add")}
          >
            Add Training
          </Dropdown.Button>
        </Space>
      </Row>
      <Col className="flex flex-auto flex-col overflow-hidden p-4 pb-4 pt-0">
        <TrainingsView
          trigger={triggerView}
          onAction={(key: string, record: any) =>
            onTrainingAction(key as any, record)
          }
          onSearch={onSearch}
        />
      </Col>
      <TrainingFormDialog
        mode={trainingInputMode === "manage" ? "add" : trainingInputMode}
        open={trainingInputOpen && trainingInputMode !== "manage"}
        record={trainingInputRecord}
        onFinish={onTrainingFormFinish}
        onCancel={onTrainingInputClose}
      />
      <TrainingManageDialog
        open={trainingInputOpen && trainingInputMode === "manage"}
        record={trainingInputRecord}
        onFinish={onTrainingInputFinish}
        onCancel={onTrainingInputClose}
        onViewEvent={openPreview}
      />
      {previewDialog}
      {contextHolder}
    </Col>
  );
}

export default TrainingsPage;
